import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';

import mergeClasses from '../../../utils/shallowMerge';
import Portal from '../portal';
import TooltipContent from './TooltipContent';
import { useStateWithTimeout } from './hooks/useStateWithTimeOut';
import defaultClasses from './scss/Tooltip.module.scss';
import { useTooltip } from './useTooltip';

const Tooltip = ({
  children,
  enabled = true,
  tabIndex = '0',
  content = null,
  preferredPosition = null,
  renderInPortal = true,
  preferredAlign = 'center',
  stopPropagation = false,
  dataTest,
  customClasses = {},
}) => {
  const styles = mergeClasses(defaultClasses, customClasses);
  const container = useRef(null);
  const [shown, setShown] = useState(false);
  const [render, setRender, setRenderWithTimeout, clearRenderTimeout] =
    useStateWithTimeout(false, 200);
  const tooltipId = 'tooltipId';
  const { handleOpen, handleClose } = useTooltip({
    setRender,
    setShown,
    stopPropagation,
    setRenderWithTimeout,
    clearRenderTimeout,
  });

  if (!enabled) return children;

  const tooltip = (
    <TooltipContent
      parent={children}
      dataTest={dataTest}
      shown={shown}
      onClose={handleClose}
      preferredPosition={preferredPosition}
      preferredAlign={preferredAlign}
      containerRef={container}
    >
      {content}
    </TooltipContent>
  );

  return (
    <>
      <div
        className={styles.tooltipHolder}
        role="tooltip"
        onClick={handleOpen}
        ref={container}
        aria-describedby={enabled ? tooltipId : undefined}
        aria-label={content}
        tabIndex={enabled ? tabIndex : undefined}
      >
        {children}
      </div>
      {enabled &&
        render &&
        (renderInPortal ? (
          <Portal renderInto="tooltips">{tooltip}</Portal>
        ) : (
          tooltip
        ))}
    </>
  );
};

Tooltip.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  content: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  enabled: PropTypes.bool,
  tabIndex: PropTypes.string,
  preferredPosition: PropTypes.string,
  customClasses: PropTypes.shape({
    root: PropTypes.string,
    tooltipHolder: PropTypes.string,
  }),
  renderInPortal: PropTypes.bool,
  preferredAlign: PropTypes.string,
  stopPropagation: PropTypes.bool,
};

export default Tooltip;
